/**
 * Error Handler Utility for VerDict AI Frontend
 * 
 * Handles structured error responses from the backend API
 * Maps error codes to user-friendly messages
 */

// Error code to user-friendly message mapping
const ERROR_MESSAGES = {
    // Authentication Errors
    'AUTH_001': 'Please log in to continue.',
    'AUTH_002': 'Your session has expired. Please log in again.',
    'AUTH_003': 'Account not found. Please register first.',
    'AUTH_004': 'Registration failed. Please try again.',

    // Query Limit Errors
    'LIMIT_001': 'You\'ve reached your query limit. Please submit feedback to continue.',
    'LIMIT_002': 'Thank you for your feedback! Your testing session is complete.',

    // Validation Errors
    'VAL_001': 'Please fill in all required fields.',
    'VAL_002': 'Please enter a valid email address.',
    'VAL_003': 'Invalid input type. Please select link, text, or image.',
    'VAL_004': 'Invalid content provided.',
    'VAL_005': 'Rating must be between 1 and 5.',

    // Feedback Errors
    'FEED_001': 'Please complete all queries before submitting feedback.',
    'FEED_002': 'You have already submitted feedback.',
    'FEED_003': 'Failed to submit feedback. Please try again.',

    // Processing Errors
    'PROC_001': 'Cache error occurred.',
    'PROC_002': 'Analysis failed. Please try again.',
    'PROC_003': 'External service error. Please try again later.',

    // System Errors
    'SYS_001': 'Database error. Please try again.',
    'SYS_002': 'Internal server error. Please try again later.',
};

/**
 * Extract error information from API response
 * @param {Object} error - Error object from axios or fetch
 * @returns {Object} - Structured error information
 */
export function parseError(error) {
    // Check if it's a structured error response
    // Support both direct data.error and FastAPI's detail.error
    const errorData = error.response?.data?.error || error.response?.data?.detail?.error;
    const status = error.response?.status;

    if (status === 429) {
        return {
            code: 'RATE_LIMIT',
            message: 'Too many requests. Please wait a minute before trying again.',
            details: {},
            isStructured: false
        };
    }

    if (errorData) {

        // Handle structured error object: { code, message, details }
        if (typeof errorData === 'object' && errorData !== null) {
            const { code, message, details } = errorData;
            return {
                code,
                message: message || ERROR_MESSAGES[code] || 'An error occurred.',
                details: details || {},
                isStructured: true
            };
        }

        // Handle simple error string: "some error message"
        if (typeof errorData === 'string') {
            return {
                code: null,
                message: errorData,
                details: {},
                isStructured: false
            };
        }
    }

    // Check for old-style error format
    if (error.response?.data?.detail) {
        return {
            code: null,
            message: error.response.data.detail,
            details: {},
            isStructured: false
        };
    }

    // Network or unknown error
    return {
        code: null,
        message: error.message || 'Connection error. Please try again.',
        details: {},
        isStructured: false
    };
}

/**
 * Get user-friendly error message
 * @param {Object} error - Error object from axios or fetch
 * @returns {string} - User-friendly error message
 */
export function getErrorMessage(error) {
    const parsedError = parseError(error);
    const message = parsedError.message;
    return typeof message === 'string' ? message : JSON.stringify(message);
}

/**
 * Check if error is a specific error code
 * @param {Object} error - Error object
 * @param {string} errorCode - Error code to check
 * @returns {boolean}
 */
export function isErrorCode(error, errorCode) {
    const parsedError = parseError(error);
    return parsedError.code === errorCode;
}

/**
 * Check if error requires user to login
 * @param {Object} error - Error object
 * @returns {boolean}
 */
export function isAuthError(error) {
    const parsedError = parseError(error);
    return parsedError.code?.startsWith('AUTH_');
}

/**
 * Check if error is due to query limit
 * @param {Object} error - Error object
 * @returns {boolean}
 */
export function isLimitError(error) {
    const parsedError = parseError(error);
    return parsedError.code?.startsWith('LIMIT_');
}

const errorHandler = {
    parseError,
    getErrorMessage,
    isErrorCode,
    isAuthError,
    isLimitError,
    ERROR_MESSAGES
};

export default errorHandler;
